import type { AppSettings, PersistedState, Subscription } from "./types";
import { categories, colorForName, isValidDateOnly, markForName } from "./lib/subscriptions";

export const STORAGE_KEY = "subly.state.v1";

export const defaultSettings: AppSettings = {
  theme: "dark",
};

export const emptyState: PersistedState = {
  version: 1,
  subscriptions: [],
  settings: defaultSettings,
};

export function loadState(): PersistedState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return emptyState;

    const parsed = JSON.parse(raw) as Partial<PersistedState>;
    if (parsed.version !== 1 || !Array.isArray(parsed.subscriptions)) return emptyState;
    const subscriptions = parsed.subscriptions.map(sanitizeSubscription).filter((sub): sub is Subscription => sub !== null);
    const settings = sanitizeSettings(parsed.settings);
    const repairedState = { version: 1, subscriptions, settings } satisfies PersistedState;
    if (JSON.stringify(repairedState) !== JSON.stringify(parsed)) backupRawState(raw);

    return repairedState;
  } catch {
    return emptyState;
  }
}

export function saveState(state: PersistedState) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    return true;
  } catch {
    return false;
  }
}

function sanitizeSettings(value: unknown): AppSettings {
  if (!value || typeof value !== "object") return defaultSettings;
  const settings = value as Record<string, unknown>;
  return {
    theme: settings.theme === "dark" || settings.theme === "light" ? settings.theme : defaultSettings.theme,
  };
}

export function sanitizeSubscription(value: unknown): Subscription | null {
  if (!value || typeof value !== "object") return null;
  const sub = value as Record<string, unknown>;
  const now = new Date().toISOString();

  if (typeof sub.id !== "string" || sub.id.trim() === "") return null;
  if (typeof sub.name !== "string" || sub.name.trim() === "") return null;
  const cleanName = clampText(sub.name, 100);
  if (cleanName === "") return null;
  if (typeof sub.price !== "number" || !Number.isFinite(sub.price) || sub.price <= 0) return null;
  const cleanPrice = Math.max(1, Math.round(sub.price));
  if (typeof sub.nextDate !== "string" || !isValidDateOnly(sub.nextDate)) return null;
  const cycle = typeof sub.cycle === "string" && ["monthly", "yearly", "weekly"].includes(sub.cycle) ? sub.cycle : "monthly";
  const category = typeof sub.category === "string" && sub.category.trim() !== "" ? clampText(sub.category, 40) : categories[0];
  const method = typeof sub.method === "string" ? clampText(sub.method, 80) || "未設定" : "未設定";
  const reminder = typeof sub.reminder === "string" && ["1d", "3d", "off"].includes(sub.reminder) ? sub.reminder : "3d";
  const color = typeof sub.color === "string" && isSafeHexColor(sub.color) ? sub.color.trim() : colorForName(cleanName);
  const mark = typeof sub.mark === "string" && isSafeMark(sub.mark) ? sub.mark.trim() : markForName(cleanName);
  if (sub.createdAt !== undefined && typeof sub.createdAt !== "string") return null;
  if (sub.updatedAt !== undefined && typeof sub.updatedAt !== "string") return null;

  return {
    id: clampText(sub.id, 80),
    name: cleanName,
    price: cleanPrice,
    cycle: cycle as Subscription["cycle"],
    category,
    nextDate: sub.nextDate,
    method,
    reminder: reminder as Subscription["reminder"],
    color,
    mark,
    createdAt: sub.createdAt ?? now,
    updatedAt: sub.updatedAt ?? sub.createdAt ?? now,
  };
}

function isSafeHexColor(value: string) {
  return /^#[0-9a-fA-F]{6}$/.test(value.trim());
}

function isSafeMark(value: string) {
  const clean = value.trim();
  return clean.length > 0 && [...clean].length <= 3;
}

function clampText(value: string, maxLength: number) {
  return [...value.trim()].slice(0, maxLength).join("");
}

function backupRawState(raw: string) {
  try {
    const baseKey = `${STORAGE_KEY}.backup.${hashString(raw)}`;
    const existing = localStorage.getItem(baseKey);
    if (existing === raw) return;
    if (existing !== null) {
      const prefix = `${baseKey}.`;
      for (let index = 0; index < localStorage.length; index += 1) {
        const key = localStorage.key(index);
        if (key?.startsWith(prefix) && localStorage.getItem(key) === raw) return;
      }

      const suffix = String(Date.now());
      let candidate = `${prefix}${suffix}`;
      let attempt = 0;
      while (localStorage.getItem(candidate) !== null) {
        attempt += 1;
        candidate = `${prefix}${suffix}.${attempt}`;
      }
      localStorage.setItem(candidate, raw);
      return;
    }
    localStorage.setItem(baseKey, raw);
  } catch {
    // Best-effort only. Loading should never fail because backup storage failed.
  }
}

export function hashString(value: string) {
  let hash = 0x811c9dc5;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(16).padStart(8, "0");
}
